/* eslint-disable react-hooks/rules-of-hooks */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Grid, IconButton, Snackbar } from '@mui/material';
import MuiAlert, { AlertColor, AlertProps } from '@mui/material/Alert';
import { ColumnDef } from '@tanstack/table-core';
import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import useModuleMetadata from 'src/hooks/use-module-metadata.hook';
import ShareIcon from '@mui/icons-material/Share';
import MODULE_TO_PAGE_MAP from '../../../../home/types/simulation-module-to-page-map';
import { editInputFieldValues } from '../../../../innovation/store/innovation-slice';
import {
  deleteSku,
  duplicateCurrSku,
  fetchOverwriteMetadata
} from '../../../../innovation/store/innovation-slice-async-thunks';
import {
  Header,
  TableComponent
} from '../../../../pricing-simulator/types/generic-table.model';
import { toggleShareScenarioDialogVisibility } from '../../../../scenario-manager/scenario-manager-slice';

export default function getColumnForActionSection(
  componentType: TableComponent,
  columnData: Header
) {
  const subHeader: ColumnDef<{
    [key: string]: number | string | { [key: string]: string }[];
  }> = {
    id:
      columnData.parent.parent_field !== ''
        ? columnData.parent.parent_field
        : columnData.parent.order.toString(),
    meta: {
      className: 'action-column last'
    },

    cell: ({ row, getValue }) => {
      const appDispatch = useAppDispatch();
      // getting user, scenario and source details from useModuleMetadata hooks
      const { moduleId, sourceId, scenarioId } = useModuleMetadata({
        module: MODULE_TO_PAGE_MAP.INNOVATION
      });

      // getting state of innovation
      const { appliedFilters, isSimulated } = useAppSelector(
        (state) => state.innovation
      );

      // local state for snackbar
      const [isSnackbarOpen, setSnackbar] = useState(false);
      const { createCurrentOverwrittenObj } = useAppSelector(
        (state) => state.innovation
      );

      const [snackbarContent, setSnackbarContent] = useState<{
        snackbarText: string;
        snackbarType: AlertColor;
      }>({
        snackbarText: '',
        snackbarType: 'success'
      });

      // Handler for snackbar
      const handleClose = (
        event: React.SyntheticEvent | Event,
        reason?: string
      ) => {
        if (reason === 'clickaway') {
          return;
        }
        setSnackbar(false);
      };

      const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
        (props, ref) => (
          <MuiAlert elevation={6} ref={ref} variant='filled' {...props} />
        )
      );
      // const [open, setOpen] = React.useState(false);
      const [isOverwriteDialogOpen, setIsOverwriteDialogOpen] = useState(false);

      // // handler for cogs dialog close
      const handleOverwriteDialogClose = () => {
        setIsOverwriteDialogOpen(false);
        // appDispatch(resetCogsFilters());
      };
      // handler for editing sku
      const onEditButtonClick = (rowData) => {
        appDispatch(editInputFieldValues(rowData));
      };
      const onDuplicateButtonClick = (rowData) => {
        setSnackbar(false);
        appDispatch(
          duplicateCurrSku({
            sourceID: sourceId,
            moduleID: moduleId,
            scenarioID: Number(scenarioId),
            skuName: rowData.original.sku,
            duplicateCount: 1,
            filters: appliedFilters
          })
        ).then((res) => {
          if (res.meta.requestStatus === 'rejected') {
            setSnackbarContent({
              // eslint-disable-next-line dot-notation
              snackbarText: res['error'].message,
              snackbarType: 'error'
            });
            setSnackbar(true);
          } else {
            setSnackbarContent({
              snackbarText: `${rowData.original.sku} duplicated successfully`,
              snackbarType: 'success'
            });
            setSnackbar(true);
          }
        });
      };
      const onDeleteButtonClick = (rowData) => {
        setSnackbar(false);
        appDispatch(
          deleteSku({
            sourceID: sourceId,
            moduleID: moduleId,
            scenarioID: Number(scenarioId),
            filters: appliedFilters,
            skuName: rowData.original.sku
          })
        ).then((res) => {
          if (res.meta.requestStatus === 'rejected') {
            setSnackbarContent({
              // eslint-disable-next-line dot-notation
              snackbarText: res['error'].message,
              snackbarType: 'error'
            });
            setSnackbar(true);
          } else {
            setSnackbarContent({
              snackbarText: `${rowData.original.sku} deleted successfully`,
              snackbarType: 'success'
            });
            setSnackbar(true);
          }
        });
      };
      const onOverwriteButtonClick = (rowData) => {
        // setting dilogue open state to true
        setIsOverwriteDialogOpen(true);

        // calling get metadata api
        appDispatch(
          fetchOverwriteMetadata({
            sourceID: sourceId,
            moduleID: moduleId,
            scenarioID: Number(scenarioId),
            skuName: rowData.original.sku
          })
        ).then((res) => {
          if (res.meta.requestStatus === 'rejected') {
            setSnackbarContent({
              // eslint-disable-next-line dot-notation
              snackbarText: res['error'].message,
              snackbarType: 'error'
            });
            setSnackbar(true);
          }
        });
      };
      const onShareButtonClick = () => {
        appDispatch(
          toggleShareScenarioDialogVisibility({
            visibility: true,
            scenario: row.original as { [key: string]: number | string }
          })
        );
      };

      const { innovation: innovationAccessConfig } = useAppSelector(
        (state) => state.simulatorHome.moduleAccessMap
      );

      return (
        <div style={{ width: '130px' }}>
          <Grid
            container
            justifyContent='space-between'
            style={{ color: '#0000A0' }}
          >
            {TableComponent.SCENARIOMANAGER !== componentType && (
              <>
                <Grid item>
                  <IconButton
                    color='primary'
                    aria-label='edit'
                    disabled={!innovationAccessConfig.edit_sku}
                    onClick={() => onEditButtonClick(row)}
                  >
                    <EditIcon style={{ fontSize: '16px' }} />
                  </IconButton>
                </Grid>
                <Grid item>
                  <IconButton
                    color='primary'
                    disabled={!innovationAccessConfig.delete_sku}
                    aria-label='delete'
                    onClick={() => onDeleteButtonClick(row)}
                  >
                    <DeleteIcon style={{ fontSize: '16px' }} />
                  </IconButton>
                </Grid>
                <Grid item>
                  <IconButton
                    color='primary'
                    disabled={
                      !innovationAccessConfig.duplicate_sku ||
                      row.original.is_incomplete === 1
                    }
                    aria-label='copy'
                    onClick={() => onDuplicateButtonClick(row)}
                  >
                    <ContentCopyIcon style={{ fontSize: '16px' }} />
                  </IconButton>
                </Grid>
              </>
            )}
            {TableComponent.SCENARIOMANAGER === componentType && (
              <Grid item>
                <IconButton
                  color='primary'
                  aria-label='copy'
                  onClick={() => onShareButtonClick()}
                >
                  <ShareIcon style={{ fontSize: '16px' }} />
                </IconButton>
              </Grid>
            )}
          </Grid>

          <Snackbar
            open={isSnackbarOpen}
            autoHideDuration={3000}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Alert
              onClose={handleClose}
              severity={snackbarContent.snackbarType}
              sx={{ width: '100%' }}
            >
              {snackbarContent.snackbarText}
            </Alert>
          </Snackbar>
        </div>
      );
    },
    header: () => ''
  };
  return subHeader;
}
